import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn } from '@angular/router';
import { MmuUserService } from '../../auth/user-service/mmu-user.service';
import { MmuLogger } from '../../logging';


/**
 * Authenticated route guard factory for Miele MOVE. Check if the user is authenticated. If authentication is required and the user is
 * not authenticated, the authentication flow is triggered.
 *
 * @param [shouldBeAuthenticated=true] Whether the user should be authenticated or not.
 * @returns {CanActivateFn | CanActivateChildFn}
 *
 * @example ```typescript
 * {
 *   path: 'protected',
 *   component: ProtectedComponent,
 *   canActivate: [checkAuthentication({ shouldBeAuthenticated: true })]
 * }
 * ```
 */
export function isAuthenticated(shouldBeAuthenticated = true): CanActivateFn | CanActivateChildFn {
  return (_, state) => {

    const userService = inject(MmuUserService);
    const allowNavigation = userService.authenticated === shouldBeAuthenticated;
    const url = new URL(window.location.href);

    if (allowNavigation) {
      return true;
    } else if (shouldBeAuthenticated && !url.searchParams.has('_auth')) {
      const logger = inject(MmuLogger);
      logger.warn(`Tried to access a protected page at "${state.url}", but the user is not logged in.`);

      /* If authentication is required and the user is not authenticated, we trigger the authentication flow here by adding the _auth
       * query parameter and reloading the page with that URL. */
      url.searchParams.set('_auth', 'true');
      window.location.href = url.toString();
    }


    return false;
  };
}
